"use client";

import { useSyncExternalStore } from "react";

/**
 * Les emplacements où un widget peut se poser sur le roll.
 *
 * # Pourquoi des ancres et pas des coordonnées
 *
 * Le roll change de taille (tiroirs, minimap qui se resserre, clavier qui
 * grandit avec la largeur), et les panneaux du parcours changent de forme d'une
 * étape à l'autre. Un widget posé en pixels finirait sous l'un d'eux à la
 * première largeur imprévue. Une ancre dit seulement « ce coin-là » ; le calque
 * empile ce qui y tombe, et c'est lui seul qui connaît la géométrie.
 *
 * # Les voisins à connaître
 *
 * - `top-center` est la place de la capsule du parcours (`FloatingShell`,
 *   variante `floating`) : à réserver aux écrans où le parcours n'est pas là.
 * - Les deux coins du haut passent sous la bande (`band`) pendant une passe, et
 *   `top-left` sous la puce de phrase.
 * - Les coins du bas sont libres de tout panneau du parcours ; seule
 *   l'invitation à donner un retour s'y centre, au milieu.
 */
export type RollWidgetAnchor =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

export const ROLL_WIDGET_ANCHORS: readonly RollWidgetAnchor[] = [
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-right",
];

/**
 * L'emplacement du widget mis en avant : agrandi, centré, par-dessus les
 * autres. Voir `roll-widget-front.ts`.
 *
 * Ce n'est pas une ancre : une ancre est un coin où un widget *habite*, et
 * celui qui est en avant garde le sien pendant ce temps — c'est là qu'il
 * retourne en sortant. Le premier plan se pose par-dessus, il ne remplace pas.
 */
export const FRONT_SLOT = "front";

/** De quoi reconnaître le premier plan dans le DOM, sans remonter de référence. */
export const FRONT_SLOT_SELECTOR = `[data-roll-widget-slot="${FRONT_SLOT}"]`;

/** Un emplacement du calque : les cinq coins, et le premier plan. */
export type RollWidgetSlot = RollWidgetAnchor | typeof FRONT_SLOT;

/**
 * Les emplacements que le calque monte, **dans l'ordre où il les écrit**.
 *
 * Le premier plan en dernier, et c'est tout ce qui le met devant : à `z` égal
 * c'est l'ordre du DOM qui peint. Rien à surélever, donc rien qui passerait
 * aussi devant les calques qui masquent le roll.
 */
export const ROLL_WIDGET_SLOTS: readonly RollWidgetSlot[] = [...ROLL_WIDGET_ANCHORS, FRONT_SLOT];

type Slots = Readonly<Partial<Record<RollWidgetSlot, HTMLElement>>>;

// Magasin de module, comme `shell-occupancy` : le calque est monté une seule
// fois par `StudioFrame`, et les widgets le rejoignent d'où qu'ils soient
// rendus sans qu'on ait à faire descendre un contexte jusqu'à eux.
let slots: Slots = {};
const listeners = new Set<() => void>();

function publishSlot(slot: RollWidgetSlot, node: HTMLElement | null): void {
  if ((slots[slot] ?? null) === node) {
    return;
  }

  // Un nouvel objet à chaque changement : `useSyncExternalStore` compare les
  // instantanés par identité.
  const next = { ...slots };

  if (node === null) {
    delete next[slot];
  } else {
    next[slot] = node;
  }

  slots = next;

  for (const listener of listeners) {
    listener();
  }
}

/**
 * Une référence stable par ancre. Une flèche écrite dans le rendu serait
 * rappelée à chaque rendu du calque, avec `null` puis le nœud : les widgets
 * perdraient leur portail et se remonteraient — un flux vidéo en tête.
 */
export const slotRefs: Readonly<
  Record<RollWidgetSlot, (node: HTMLElement | null) => void>
> = {
  "top-left": (node) => publishSlot("top-left", node),
  "top-center": (node) => publishSlot("top-center", node),
  "top-right": (node) => publishSlot("top-right", node),
  "bottom-left": (node) => publishSlot("bottom-left", node),
  "bottom-right": (node) => publishSlot("bottom-right", node),
  front: (node) => publishSlot(FRONT_SLOT, node),
};

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Slots {
  return slots;
}

const EMPTY: Slots = {};

/** Le nœud d'un emplacement, ou `null` tant que le calque n'est pas monté. */
export function useRollWidgetSlot(slot: RollWidgetSlot): HTMLElement | null {
  const current = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);

  return current[slot] ?? null;
}
